// src/services/api.js
import axios from "axios";
import { getCache, setCache } from "./apiCache";

const API_BASE_URL = process.env.VUE_APP_API_BASE_URL;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// トークン設定用の関数
export const setAuthToken = (token) => {
  if (token) {
    api.defaults.headers.common["Authorization"] = `Token ${token}`;
  } else {
    delete api.defaults.headers.common["Authorization"];
  }
};

// 401エラーをインターセプトしてログインページにリダイレクト
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      sessionStorage.removeItem("token");
      localStorage.removeItem("userId");
      localStorage.removeItem("username");
      localStorage.removeItem("isStaff");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

export const login = async (username, password) => {
  const response = await api.post("api-token-auth/", { username, password });
  return response.data;
};

// パスワード変更
export const changePassword = async (oldPassword, newPassword, newPasswordConfirm) => {
  const response = await api.post("password/change/", {
    old_password: oldPassword,
    new_password: newPassword,
    new_password_confirm: newPasswordConfirm
  });
  return response.data;
};

// パスワードリセットリクエスト
export const requestPasswordReset = async (email) => {
  const response = await api.post("password/reset/", { email });
  return response.data;
};

// パスワードリセット確認
export const confirmPasswordReset = async (token, uidb64, newPassword, newPasswordConfirm) => {
  const response = await api.post("password/reset/confirm/", {
    token: token,
    uidb64: uidb64,
    new_password: newPassword,
    new_password_confirm: newPasswordConfirm
  });
  return response.data;
};

export const clockIn = async (data) => {
  return await api.post("attendance/clock_in/", data);
};

export const clockOut = async () => {
  return await api.post("attendance/clock_out/");
};

export const reserveSeat = async (date, seatNumber) => {
  return await api.post("seats/reserve/", { date, seat_number: seatNumber });
};

export const cancelSeatReservation = async (reservationId) => {
  return await api.delete(`seats/reservations/${reservationId}/cancel/`);
};

export const applyDayOff = async (data) => {
  return await api.post("dayoff/apply/", data);
};

export async function fetchCurrentUser() {
  const res = await api.get("/current_user/");
  const { id, username, admin, is_staff, is_attendance_admin, has_approval_right, is_top_level } = res.data;
  localStorage.setItem("userId", id);
  localStorage.setItem("username", username);
  localStorage.setItem("isAdmin", admin);
  localStorage.setItem("isStaff", is_staff);
  localStorage.setItem("isAttendanceAdmin", is_attendance_admin); // isAttendanceAdmin を追加
  localStorage.setItem("hasApprovalRight", has_approval_right); // hasApprovalRight を追加
  localStorage.setItem("isTopLevel", is_top_level); // isTopLevel を追加
  return res.data;
}

export const fetchMeetingRooms = async () => {
  return await api.get("meeting/rooms/");
};

export const fetchMeetingReservations = async () => {
  return await api.get("meeting/reservations/");
};

export const createMeetingReservation = async (data) => {
  return await api.post("meeting/reservations/", data);
};

export const deleteMeetingReservation = async (id) => {
  return await api.delete(`meeting/reservations/${id}/`);
};

export const fetchSchedule = async (year, month) => {
  return await api.get("schedule/", { params: { year, month } });
};

export const fetchHolidays = async () => {
  const cacheKey = "holidays";
  const cachedData = getCache(cacheKey);
  if (cachedData) {
    return cachedData;
  }
  const response = await api.get("common/holidays/");
  setCache(cacheKey, response, 90 * 24 * 60 * 60 * 1000); // 3ヶ月
  return response;
};

export const fetchTeams = async () => {
  const cacheKey = "teams";
  const cachedData = getCache(cacheKey);
  if (cachedData) {
    return cachedData;
  }
  const response = await api.get("teams/teams/");
  setCache(cacheKey, response, 24 * 60 * 60 * 1000); // 24時間
  return response;
};

export const fetchSeatLabels = async () => {
  const cacheKey = "seatLabels";
  const cachedData = getCache(cacheKey);
  if (cachedData) {
    return cachedData;
  }
  const response = await api.get("common/seat-labels/");
  setCache(cacheKey, response, 24 * 60 * 60 * 1000); // 24時間
  return response;
};

export const fetchProfile = async () => {
  const cacheKey = "profile";
  const cachedData = getCache(cacheKey);
  if (cachedData) {
    return cachedData;
  }
  const response = await api.get("profile/");
  setCache(cacheKey, response, 24 * 60 * 60 * 1000); // 24時間
  return response;
};

export const fetchUsers = async () => {
  const cacheKey = "users";
  const cachedData = getCache(cacheKey);
  if (cachedData) {
    return cachedData;
  }
  const response = await api.get("users/");
  setCache(cacheKey, response, 24 * 60 * 60 * 1000); // 24時間
  return response;
};

export const fetchAttendance = async (userId, year, month, forceRefresh = false) => {
  const cacheKey = `attendance-${userId}-${year}-${month}`;
  if (!forceRefresh) {
    const cachedData = getCache(cacheKey);
    if (cachedData) {
      return cachedData;
    }
  }
  const response = await api.get("attendance/", {
    params: {
      user: userId,
      year: year,
      month: month,
    },
  });
  setCache(cacheKey, response, 24 * 60 * 60 * 1000); // 24時間
  return response;
};

export const fetchVisibleAttendanceUsers = async () => {
  return await api.get("attendance/visible-users/");
};

export const fetchManagerPendingApprovals = async () => {
  return await api.get("attendance/manager-pending/");
};

export default api;
